'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { consumeToolUsage } from '@/lib/browser-tool-usage';

const VALUES: Array<[number, string]> = [
  [1000, 'M'],
  [900, 'CM'],
  [500, 'D'],
  [400, 'CD'],
  [100, 'C'],
  [90, 'XC'],
  [50, 'L'],
  [40, 'XL'],
  [10, 'X'],
  [9, 'IX'],
  [5, 'V'],
  [4, 'IV'],
  [1, 'I'],
];

// The classical alphabet stops at 3999. Above that, Romans drew a vinculum (an overline) over a
// numeral to multiply it by 1000, which is why "39999 in roman numerals" and "extended roman
// numerals" are real searches that most converters answer with an error. We support it: the
// overlined part is rendered with a real overline, and copied as the conventional ASCII
// stand-in `(XXXIX)` so it survives being pasted into plain text.
const MAX_VALUE = 3_999_999;

function toRomanBasic(value: number): string {
  let remaining = value;
  let out = '';
  for (const [amount, symbol] of VALUES) {
    while (remaining >= amount) {
      out += symbol;
      remaining -= amount;
    }
  }
  return out;
}

type Roman = { thousands: string; units: string };

function toRoman(value: number): Roman {
  if (value < 4000) return { thousands: '', units: toRomanBasic(value) };
  return {
    thousands: toRomanBasic(Math.floor(value / 1000)),
    units: toRomanBasic(value % 1000),
  };
}

function romanToAscii({ thousands, units }: Roman): string {
  return thousands ? `(${thousands})${units}` : units;
}

function parseBasic(cleaned: string): number | null {
  if (!/^[MDCLXVI]+$/.test(cleaned)) return null;
  let index = 0;
  let value = 0;
  for (const [amount, symbol] of VALUES) {
    while (cleaned.startsWith(symbol, index)) {
      value += amount;
      index += symbol.length;
    }
  }
  if (index !== cleaned.length || value < 1) return null;
  return toRomanBasic(value) === cleaned ? value : null;
}

function fromRoman(input: string): { value: number | null; error: string | null } {
  // Accept the overline form people paste from elsewhere (combining macron) as well as (XXXIX).
  const cleaned = input
    .toUpperCase()
    .replace(/[\s.]/g, '')
    .replace(/([MDCLXVI])̄/g, '$1');
  if (!cleaned) return { value: null, error: null };

  const vinculum = cleaned.match(/^\(([MDCLXVI]+)\)([MDCLXVI]*)$/);
  if (vinculum) {
    const high = parseBasic(vinculum[1]);
    const low = vinculum[2] ? parseBasic(vinculum[2]) : 0;
    if (high == null || low == null) {
      return { value: null, error: 'That is not a well-formed Roman numeral.' };
    }
    const value = high * 1000 + low;
    if (value > MAX_VALUE) {
      return { value: null, error: `The largest value this notation covers is ${MAX_VALUE}.` };
    }
    return { value, error: null };
  }

  if (!/^[MDCLXVI]+$/.test(cleaned)) {
    return { value: null, error: 'Only the letters M, D, C, L, X, V and I are valid.' };
  }

  const value = parseBasic(cleaned);
  if (value == null) {
    // Work out what they probably meant so the message is useful rather than just a rejection.
    let index = 0;
    let loose = 0;
    for (const [amount, symbol] of VALUES) {
      while (cleaned.startsWith(symbol, index)) {
        loose += amount;
        index += symbol.length;
      }
    }
    if (index === cleaned.length && loose > 0) {
      return {
        value: loose,
        error: `Non-standard form. The canonical spelling of ${loose} is ${toRomanBasic(loose)}.`,
      };
    }
    return { value: null, error: 'That is not a well-formed Roman numeral.' };
  }
  return { value, error: null };
}

async function copyText(value: string) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }
  const textarea = document.createElement('textarea');
  textarea.value = value;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'absolute';
  textarea.style.left = '-9999px';
  document.body.append(textarea);
  textarea.select();
  document.execCommand('copy');
  textarea.remove();
}

const CHART: Array<[string, string]> = [
  ['I', '1'],
  ['V', '5'],
  ['X', '10'],
  ['L', '50'],
  ['C', '100'],
  ['D', '500'],
  ['M', '1000'],
];

const SUBTRACTIVE: Array<[string, string]> = [
  ['IV', '4'],
  ['IX', '9'],
  ['XL', '40'],
  ['XC', '90'],
  ['CD', '400'],
  ['CM', '900'],
];

export default function BrowserRomanNumeralConverter() {
  const usageReported = useRef(false);
  const [number, setNumber] = useState('1994');
  const [roman, setRoman] = useState('MCMXCIV');
  const [lastEdited, setLastEdited] = useState<'number' | 'roman'>('number');
  const [copied, setCopied] = useState<'number' | 'roman' | null>(null);

  const result = useMemo(() => {
    if (lastEdited === 'number') {
      const raw = number.replace(/[\s,]/g, '');
      const value = Number.parseInt(raw, 10);
      if (!raw.trim()) return { roman: { thousands: '', units: '' }, number, error: null };
      if (!/^\d+$/.test(raw) || value < 1 || value > MAX_VALUE) {
        return {
          roman: { thousands: '', units: '' },
          number,
          error: `Enter a whole number from 1 to ${MAX_VALUE.toLocaleString('en-US')}.`,
        };
      }
      return { roman: toRoman(value), number, error: null };
    }
    const parsed = fromRoman(roman);
    return {
      roman: parsed.value != null ? toRoman(parsed.value) : { thousands: '', units: '' },
      number: parsed.value != null ? String(parsed.value) : '',
      error: parsed.error,
    };
  }, [number, roman, lastEdited]);

  const romanAscii = romanToAscii(result.roman);
  const numberValue = lastEdited === 'number' ? number : result.number;

  useEffect(() => {
    if (!usageReported.current && (number.trim() || roman.trim())) {
      usageReported.current = true;
      void consumeToolUsage('roman-numeral-converter', '').catch(() => undefined);
    }
  }, [number, roman]);

  useEffect(() => {
    if (!copied) return;
    const timer = window.setTimeout(() => setCopied(null), 1400);
    return () => window.clearTimeout(timer);
  }, [copied]);

  function swap() {
    // Send whatever is currently showing in the other box back through the opposite direction.
    if (lastEdited === 'number') {
      setRoman(romanAscii);
      setLastEdited('roman');
    } else {
      setNumber(result.number);
      setLastEdited('number');
    }
  }

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">Converter</span>
        <h2 className="tool-live-title">Roman numeral converter</h2>
        <p className="tool-live-subtitle">
          Converts both ways, numbers to Roman numerals and Roman numerals back to numbers, from 1
          to {MAX_VALUE.toLocaleString('en-US')}. Runs entirely in your browser.
        </p>
      </div>

      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <label className="tool-live-field tool-live-field-full">
            <span>Number</span>
            <input
              inputMode="numeric"
              onChange={(event) => {
                setNumber(event.target.value);
                setLastEdited('number');
              }}
              placeholder="1994"
              type="text"
              value={numberValue}
            />
          </label>

          <label className="tool-live-field tool-live-field-full">
            <span>Roman numeral</span>
            <input
              onChange={(event) => {
                setRoman(event.target.value);
                setLastEdited('roman');
              }}
              placeholder="MCMXCIV"
              spellCheck={false}
              style={{ fontSize: '1.15rem', letterSpacing: '0.06em' }}
              type="text"
              value={lastEdited === 'roman' ? roman : romanAscii}
            />
          </label>

          {result.roman.thousands ? (
            // .tool-live-panel-note is `display: grid`, so every child becomes its own row.
            // The numeral has to be ONE element or the overlined part and the remainder split
            // onto separate lines.
            <div className="tool-live-panel-note">
              <span style={{ fontSize: '0.78rem', textTransform: 'uppercase', opacity: 0.65 }}>
                With vinculum
              </span>
              <span style={{ fontSize: '1.15rem', letterSpacing: '0.06em' }}>
                {/* `.tool-live-panel-note span` sets font-size 0.88rem on every descendant
                    span, so the inline size has to be repeated here or the overlined part
                    renders smaller than the rest of the numeral. */}
                <span style={{ fontSize: '1.15rem', textDecoration: 'overline' }}>
                  {result.roman.thousands}
                </span>
                {result.roman.units}
              </span>
              <span style={{ opacity: 0.7 }}>
                The overline multiplies by 1,000. Plain text cannot carry it, so it copies as{' '}
                {romanAscii}.
              </span>
            </div>
          ) : null}

          {result.error ? (
            <p className="support-feedback support-feedback-error">{result.error}</p>
          ) : null}

          <div className="tool-live-actions">
            <button className="button button-secondary" onClick={swap} type="button">
              Swap direction
            </button>
            <button
              className="button button-secondary"
              disabled={!romanAscii}
              onClick={() => {
                void copyText(romanAscii);
                setCopied('roman');
              }}
              type="button"
            >
              {copied === 'roman' ? 'Copied' : 'Copy numeral'}
            </button>
            <button
              className="button button-secondary"
              disabled={!numberValue}
              onClick={() => {
                void copyText(numberValue);
                setCopied('number');
              }}
              type="button"
            >
              {copied === 'number' ? 'Copied' : 'Copy number'}
            </button>
          </div>
        </section>

        <aside className="tool-live-panel">
          {/* .tool-live-stats is a single-column grid; seven full-width rows would make this
              panel twice the height of the controls beside it. Override to a compact grid. */}
          <div
            className="tool-live-stats"
            style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(5.5rem, 1fr))', gap: '0.5rem' }}
          >
            {CHART.map(([symbol, value]) => (
              <div key={symbol}>
                <strong>{symbol}</strong>
                <span>{value}</span>
              </div>
            ))}
          </div>

          <p className="tool-live-panel-note">
            Subtractive pairs: {SUBTRACTIVE.map(([pair, value]) => `${pair} = ${value}`).join(', ')}
            . A smaller symbol before a larger one subtracts, so IV is 4 rather than IIII.
          </p>

          <div className="tool-live-stats">
            {[
              ['MMXXVI', '2026'],
              ['MCMXCIV', '1994'],
              ['(XXXIX)CMXCIX', '39,999'],
            ].map(([numeral, value]) => (
              <div key={numeral}>
                <strong>{numeral}</strong>
                <span>{value}</span>
              </div>
            ))}
          </div>
        </aside>
      </div>
    </div>
  );
}
